/**
 * Rug Pad Add-on System
 * Handles automatic size matching, pricing, and cart integration
 */

class RugPadAddon extends HTMLElement {
  constructor() {
    super();
    
    this.checkbox = this.querySelector('.rug-pad-addon__checkbox');
    this.tooltip = this.querySelector('.rug-pad-addon__tooltip');
    this.tooltipTrigger = this.querySelector('.rug-pad-addon__tooltip-trigger');
    this.priceDisplay = this.querySelector('[data-price-display]');
    this.originalPriceEl = this.querySelector('[data-original-price]');
    this.currentPriceEl = this.querySelector('[data-current-price]');
    this.unavailableMessage = this.querySelector('[data-unavailable-message]');
    
    // Parse configuration
    this.padVariants = JSON.parse(this.querySelector('[data-pad-variants]').textContent);
    this.config = JSON.parse(this.querySelector('[data-pad-config]').textContent);
    
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.productId;
    this.context = this.dataset.context;
    
    // State
    this.selectedPadVariant = null;
    this.mainForm = null;
    
    this.init();
  }
  
  init() {
    // Find the product form
    this.mainForm = this.findProductForm();
    if (!this.mainForm) return;
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initial update
    this.updatePadSelection();
    
    // Sync with other instances (main/sticky)
    this.setupCrossInstanceSync();
  }
  
  findProductForm() {
    // Look for the form in the parent product-info or sticky bar
    const productInfo = this.closest('product-info');
    if (productInfo) {
      return productInfo.querySelector('form[is="product-form"]');
    }
    
    // For sticky context, find the form differently
    const stickyBar = this.closest('.sticky-product-bar');
    if (stickyBar) {
      // The main form might be referenced by ID
      const mainFormId = `ProductForm-${this.sectionId}-${this.productId}`;
      return document.getElementById(mainFormId);
    }
    
    return null;
  }
  
  setupEventListeners() {
    // Tooltip interactions
    if (this.tooltipTrigger && this.tooltip) {
      this.tooltipTrigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleTooltip();
      });
      
      // Close tooltip when clicking outside
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) {
          this.closeTooltip();
        }
      });
    }
    
    // Listen for variant changes
    document.addEventListener('variant:change', () => this.updatePadSelection());
    
    // Listen for form changes (variant selectors)
    if (this.mainForm) {
      this.mainForm.addEventListener('change', (e) => {
        if (e.target.closest('[data-option-slug]') || e.target.name === 'id') {
          this.updatePadSelection();
        }
      });
    }
    
    // Intercept form submission to add pad if checked
    if (this.mainForm) {
      this.mainForm.addEventListener('submit', (e) => this.handleFormSubmit(e));
    }
  }
  
  setupCrossInstanceSync() {
    // When this checkbox changes, sync other instances
    this.checkbox.addEventListener('change', () => {
      const otherInstances = document.querySelectorAll(`rug-pad-addon[data-product-id="${this.productId}"]`);
      otherInstances.forEach(instance => {
        if (instance !== this) {
          const otherCheckbox = instance.querySelector('.rug-pad-addon__checkbox');
          if (otherCheckbox) {
            otherCheckbox.checked = this.checkbox.checked;
          }
        }
      });
    });
  }
  
  updatePadSelection() {
    const selectedSize = this.getSelectedSize();
    
    if (!selectedSize) {
      this.disablePad('Size not selected');
      return;
    }
    
    // Find matching pad variant
    const padVariant = this.findPadVariantForSize(selectedSize);
    
    if (!padVariant) {
      this.disablePad('Rug pad not available for this size');
      return;
    }
    
    if (!padVariant.available) {
      this.disablePad('Rug pad out of stock for this size');
      return;
    }
    
    // Enable and update pricing
    this.enablePad(padVariant);
  }
  
  getSelectedSize() {
    if (!this.mainForm) return null;
    
    // Try to find size from variant selectors
    const variantSelects = document.querySelector(`#VariantPicker-${this.sectionId}-${this.productId}`);
    if (!variantSelects) return null;
    
    // Look for size option (common names: Size, Dimensions, etc.)
    const sizeField = variantSelects.querySelector('[data-option-slug*="size"], [data-option-slug*="dimension"]');
    if (!sizeField) {
      // Fallback: check all options for size-related keywords
      const allFields = variantSelects.querySelectorAll('[data-option-slug]');
      for (const field of allFields) {
        const optionName = field.querySelector('.form__label')?.textContent?.toLowerCase() || '';
        if (optionName.includes('size') || optionName.includes('dimension')) {
          const sizeField = field;
          break;
        }
      }
    }
    
    if (!sizeField) return null;
    
    // Get selected value from radio or select
    const checkedRadio = sizeField.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      return checkedRadio.getAttribute('data-option-value') || checkedRadio.value;
    }
    
    const select = sizeField.querySelector('select');
    if (select) {
      return select.value;
    }
    
    return null;
  }
  
  findPadVariantForSize(sizeText) {
    if (!sizeText) return null;
    
    // Check if there's a custom variant map
    const variantMap = this.config.variantMap || {};
    
    // First, try exact match with variant map
    if (variantMap[sizeText]) {
      const mappedTitle = variantMap[sizeText];
      const variant = this.padVariants.find(v => v.title === mappedTitle);
      if (variant) return variant;
    }
    
    // Try exact title match
    let variant = this.padVariants.find(v => v.title === sizeText);
    if (variant) return variant;
    
    // Try normalized matching (remove special characters, lowercase)
    const normalizedSize = sizeText.toLowerCase().replace(/[^a-z0-9]/g, '');
    variant = this.padVariants.find(v => {
      const normalizedVariantTitle = v.title.toLowerCase().replace(/[^a-z0-9]/g, '');
      return normalizedVariantTitle === normalizedSize;
    });
    if (variant) return variant;
    
    // Try contains matching for common patterns
    // e.g., "5' x 7'" might match "5x7 Rug Pad"
    const sizeNumbers = sizeText.match(/\d+/g);
    if (sizeNumbers && sizeNumbers.length >= 2) {
      const sizePattern = sizeNumbers.join('x');
      variant = this.padVariants.find(v => v.title.includes(sizePattern));
      if (variant) return variant;
    }
    
    return null;
  }
  
  enablePad(padVariant) {
    this.selectedPadVariant = padVariant;
    this.checkbox.disabled = false;
    this.checkbox.dataset.padVariantId = padVariant.id;
    
    // Update pricing display
    const discountPercent = this.config.discountPercent || 0;
    let displayPrice = padVariant.price;
    let originalPrice = padVariant.comparePrice || padVariant.price;
    
    if (discountPercent > 0 && originalPrice === padVariant.price) {
      // Apply discount if no compare price exists
      displayPrice = Math.round(padVariant.price * (1 - discountPercent / 100));
      originalPrice = padVariant.price;
    }
    
    // Format prices
    const formatMoney = (cents) => {
      if (typeof theme !== 'undefined' && theme.utils && theme.utils.formatMoney) {
        return theme.utils.formatMoney(cents);
      }
      // Fallback formatting
      return '$' + (cents / 100).toFixed(2);
    };
    
    this.currentPriceEl.textContent = `+${formatMoney(displayPrice)}`;
    
    if (originalPrice > displayPrice) {
      this.originalPriceEl.textContent = formatMoney(originalPrice);
      this.originalPriceEl.hidden = false;
    } else {
      this.originalPriceEl.hidden = true;
    }
    
    // Hide unavailable message
    if (this.unavailableMessage) {
      this.unavailableMessage.hidden = true;
    }
    
    // Show price display
    this.priceDisplay.hidden = false;
  }
  
  disablePad(reason) {
    this.selectedPadVariant = null;
    this.checkbox.disabled = true;
    this.checkbox.checked = false;
    delete this.checkbox.dataset.padVariantId;
    
    // Hide price display
    this.priceDisplay.hidden = true;
    
    // Show unavailable message if applicable
    if (this.unavailableMessage && reason.includes('not available')) {
      this.unavailableMessage.hidden = false;
    }
  }
  
  toggleTooltip() {
    const isOpen = this.tooltip.hidden === false;
    if (isOpen) {
      this.closeTooltip();
    } else {
      this.openTooltip();
    }
  }
  
  openTooltip() {
    this.tooltip.hidden = false;
    this.tooltipTrigger.setAttribute('aria-expanded', 'true');
  }
  
  closeTooltip() {
    this.tooltip.hidden = true;
    this.tooltipTrigger.setAttribute('aria-expanded', 'false');
  }
  
  handleFormSubmit(e) {
    // Only intercept if checkbox is checked and we have a valid pad variant
    if (!this.checkbox.checked || !this.selectedPadVariant) {
      return; // Let the form submit normally
    }
    
    e.preventDefault();
    
    // Get main product details
    const mainVariantId = this.mainForm.querySelector('input[name="id"]').value;
    const quantityInput = this.mainForm.querySelector('input[name="quantity"]');
    const quantity = quantityInput ? parseInt(quantityInput.value || '1', 10) : 1;
    
    // Prepare items for multi-add
    const items = [
      {
        id: mainVariantId,
        quantity: quantity
      },
      {
        id: this.selectedPadVariant.id,
        quantity: quantity,
        properties: {
          '_rug_pad_for': `${this.config.padTitle} for ${window.location.pathname}`,
          '_parent_variant': mainVariantId
        }
      }
    ];
    
    // Use the theme's cart API or fallback to fetch
    this.addItemsToCart(items);
  }
  
  addItemsToCart(items) {
    // Show loading state
    const submitButton = this.mainForm.querySelector('button[type="submit"]');
    const originalText = submitButton?.querySelector('.btn-text')?.textContent;
    if (submitButton) {
      const btnText = submitButton.querySelector('.btn-text');
      if (btnText) btnText.textContent = 'Adding...';
      submitButton.disabled = true;
    }
    
    // Prepare the request
    const formData = {
      items: items,
      sections: this.getSectionsToRender()
    };
    
    fetch(window.theme?.routes?.cart_add_url || '/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
    .then(response => response.json())
    .then(data => {
      // Success - trigger cart update events
      if (window.theme?.pubsub) {
        window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { 
          source: 'rug-pad-addon', 
          cart: data 
        });
      }
      
      // Dispatch custom event for other scripts
      document.dispatchEvent(new CustomEvent('cart:added', { 
        detail: { items: items, cart: data } 
      }));
      
      // Open cart drawer if it exists
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.open();
      }
      
      // Reset checkbox
      this.checkbox.checked = false;
    })
    .catch(error => {
      console.error('Error adding items to cart:', error);
      alert('There was an error adding items to your cart. Please try again.');
    })
    .finally(() => {
      // Restore button state
      if (submitButton) {
        const btnText = submitButton.querySelector('.btn-text');
        if (btnText && originalText) btnText.textContent = originalText;
        submitButton.disabled = false;
      }
    });
  }
  
  getSectionsToRender() {
    // Get sections that need to be updated (for theme compatibility)
    const sections = [];
    
    // Cart drawer
    const cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer) {
      sections.push({
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '.cart-drawer'
      });
    }
    
    // Cart icon bubble
    const cartIconBubble = document.querySelector('.cart-icon-bubble');
    if (cartIconBubble) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.cart-icon-bubble'
      });
    }
    
    return sections.map(section => section.id);
  }
}

// Register the custom element
customElements.define('rug-pad-addon', RugPadAddon);

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  // Ensure all rug pad addons are initialized
  document.querySelectorAll('rug-pad-addon').forEach(addon => {
    if (!addon.initialized) {
      addon.initialized = true;
    }
  });
});

// Re-initialize on Shopify section reload (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const section = event.target;
  const addons = section.querySelectorAll('rug-pad-addon');
  addons.forEach(addon => {
    if (!addon.initialized) {
      addon.initialized = true;
    }
  });
});
